/**
 * Delivery Integrations Page
 *
 * Permite al usuario:
 * - Ver plataformas de delivery disponibles
 * - Configurar integraciones (PedidosYa, Rappi, etc.)
 * - Habilitar/deshabilitar integraciones
 * - Configurar auto-aceptar y tiempos de preparación
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDeliveryIntegrations,
  updateDeliveryIntegrationConfig,
  getSubscriptions,
  getAddons,
  toggleAddon,
} from '../services/api';
import Icon from '../components/Icon';
import UpgradePrompt from '../components/UpgradePrompt';
import { useAuth } from '../context/AuthContext';
import { storage } from '../utils/storage';
import { visiblePlatforms } from './deliveryPlatforms';
import './DeliveryIntegrationsPage.css';

function DeliveryIntegrationsPage() {
  const { t } = useTranslation();
  const { organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Feature gate (403 availableAsAddOn) — when set, render <UpgradePrompt> instead of the grid
  const [gateInfo, setGateInfo] = useState(null);
  const [activatingAddOn, setActivatingAddOn] = useState(false);

  const organizationId = organization?.organizationId;

  // Modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    externalStoreId: '',
    autoAccept: false,
    defaultPrepTime: 25,
    commissionPercent: 0,
    mode: 'marketplace',
  });
  const [saving, setSaving] = useState(false);

  // Get restaurantId from localStorage
  const restaurantId = storage.get('selectedRestaurantId');

  // Build the feature-gate info from a 403 response so we can render <UpgradePrompt>.
  // Enriches the gate body with the current plan + matching add-on (id/price) so the
  // CTA can decide between "activate add-on" vs "upgrade plan".
  const buildGateInfo = useCallback(
    async (gateBody) => {
      const info = {
        feature: gateBody.feature,
        requiredPlan: gateBody.requiredPlan,
        availableAsAddOn: !!gateBody.availableAsAddOn,
        addOnFromPlan: gateBody.addOnFromPlan || null,
        currentPlan: null,
        subscriptionId: null,
        addOnId: null,
        addOnPrice: null,
      };

      if (!organizationId) return info;

      try {
        const subsResponse = await getSubscriptions({ organizationId });
        const activeSub = (subsResponse.subscriptions || []).find(
          (sub) => sub.status === 'active' || sub.status === 'trial'
        );
        if (activeSub) {
          info.currentPlan = activeSub.planId;
          info.subscriptionId = activeSub.subscriptionId;

          // Find the add-on whose feature flag matches this gated feature.
          const flagKey = `${gateBody.feature}Enabled`;
          const addonsResponse = await getAddons(activeSub.planId).catch(() => ({ data: [] }));
          const match = (addonsResponse.data || []).find((a) => a.featureFlags?.[flagKey] === true);
          if (match) {
            info.addOnId = match.addOnId;
            info.addOnPrice = match.pricing?.USD?.monthly ?? null;
          }
        }
      } catch (subErr) {
        console.error('Error resolving plan/add-on for upgrade prompt:', subErr);
      }

      return info;
    },
    [organizationId]
  );

  // Load integrations data
  const loadIntegrations = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setGateInfo(null);
      const data = await getDeliveryIntegrations(restaurantId);
      // Convert array to object keyed by platform
      const integrationsMap = {};
      if (data.integrations) {
        data.integrations.forEach((integration) => {
          integrationsMap[integration.platform] = integration;
        });
      }
      setIntegrations(integrationsMap);
    } catch (err) {
      console.error('Error loading integrations:', err);
      // Feature gate: backend blocked delivery for the current plan but it may be
      // activatable as an add-on. Render an UpgradePrompt instead of a raw error.
      if (err.response?.status === 403 && err.response?.data?.feature) {
        const info = await buildGateInfo(err.response.data);
        setGateInfo(info);
      } else if (err.response?.status !== 404) {
        // Don't show error for 404 - just means no integrations configured yet
        setError(t('delivery.loadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [restaurantId, t, buildGateInfo]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  // Activate the add-on inline from the upgrade prompt, then reload the page.
  const handleActivateAddOn = async () => {
    if (!gateInfo?.subscriptionId || !gateInfo?.addOnId) return;
    setError('');
    try {
      setActivatingAddOn(true);
      await toggleAddon(gateInfo.subscriptionId, gateInfo.addOnId, true);
      setSuccess(t('upgrade.addOnActivated'));
      setTimeout(() => setSuccess(''), 4000);
      await loadIntegrations();
    } catch (err) {
      console.error('Error activating add-on:', err);
      setError(err.response?.data?.message || t('upgrade.activateError'));
    } finally {
      setActivatingAddOn(false);
    }
  };

  // Open configuration modal
  const handleOpenConfig = async (platform) => {
    setSelectedPlatform(platform);
    setError('');

    // Get existing config or use defaults
    const existingConfig = integrations[platform.id];
    if (existingConfig) {
      setConfigData({
        enabled: existingConfig.enabled || false,
        externalStoreId: existingConfig.externalStoreId || '',
        autoAccept: existingConfig.autoAccept || false,
        defaultPrepTime: existingConfig.defaultPrepTime || 25,
        commissionPercent: existingConfig.commissionPercent || 0,
        mode: existingConfig.mode || 'marketplace',
      });
    } else {
      setConfigData({
        enabled: false,
        externalStoreId: '',
        autoAccept: false,
        defaultPrepTime: 25,
        commissionPercent: 0,
        mode: 'marketplace',
      });
    }

    setShowConfigModal(true);
  };

  // Save configuration
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await updateDeliveryIntegrationConfig(restaurantId, selectedPlatform.id, configData);

      // Reload integrations
      await loadIntegrations();

      setSuccess(t('delivery.saveSuccess'));
      setShowConfigModal(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.response?.data?.message || t('delivery.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Get integration status
  const getIntegrationStatus = (platformId) => {
    const integration = integrations[platformId];
    if (!integration) return 'notConfigured';
    return integration.enabled ? 'active' : 'inactive';
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'inactive':
        return 'badge-warning';
      default:
        return 'badge-default';
    }
  };

  if (loading) {
    return (
      <div className="delivery-page">
        <div className="page-header">
          <h1>{t('delivery.title')}</h1>
        </div>
        <div className="loading-container">
          <Icon name="sync" className="spin" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="delivery-page">
      <div className="page-header">
        <h1>{t('delivery.title')}</h1>
        <p>{t('delivery.subtitle')}</p>
      </div>

      {/* Success/Error messages */}
      {success && (
        <div className="alert alert-success">
          <Icon name="check_circle" />
          <span>{success}</span>
        </div>
      )}

      {error && !showConfigModal && (
        <div className="alert alert-error">
          <Icon name="error" />
          <span>{error}</span>
        </div>
      )}

      {/* Feature gate — delivery not in current plan but maybe activatable as add-on */}
      {gateInfo && (
        <UpgradePrompt
          feature={gateInfo.feature}
          requiredPlan={gateInfo.requiredPlan}
          availableAsAddOn={gateInfo.availableAsAddOn}
          addOnFromPlan={gateInfo.addOnFromPlan}
          currentPlan={gateInfo.currentPlan}
          addOnPrice={gateInfo.addOnPrice}
          onActivateAddOn={gateInfo.addOnId ? handleActivateAddOn : undefined}
          activating={activatingAddOn}
        />
      )}

      {/* Platforms Grid */}
      {!gateInfo && (
        <div className="platforms-grid">
          {visiblePlatforms.map((platform) => {
            const status = getIntegrationStatus(platform.id);
            const integration = integrations[platform.id];

            return (
              <div
                key={platform.id}
                className={`platform-card ${!platform.available ? 'disabled' : ''}`}
                style={{ '--platform-color': platform.color }}
              >
                <div className="platform-header">
                  <span className="platform-icon">{platform.icon}</span>
                  <h3>{platform.name}</h3>
                </div>

                {platform.available ? (
                  <>
                    <div className="platform-status">
                      <span className={`status-badge ${getStatusBadgeClass(status)}`}>
                        {t(`delivery.status.${status}`)}
                      </span>
                    </div>

                    {integration && integration.enabled && (
                      <div className="platform-info">
                        {platform.reusesMpCredentials ? (
                          <div className="info-item">
                            <Icon name="credit_card" size={16} />
                            <span>{t('delivery.platforms.mercadoEnvios.reusesMp')}</span>
                          </div>
                        ) : (
                          <div className="info-item">
                            <Icon name="store" size={16} />
                            <span>{integration.externalStoreId || '-'}</span>
                          </div>
                        )}
                        <div className="info-item">
                          <Icon name="smart_toy" size={16} />
                          <span>
                            {integration.autoAccept
                              ? t('delivery.config.autoAcceptEnabled')
                              : t('delivery.config.autoAcceptDisabled')}
                          </span>
                        </div>
                        <div className="info-item">
                          <Icon name="schedule" size={16} />
                          <span>{integration.defaultPrepTime || 25} min</span>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary platform-btn"
                      onClick={() => handleOpenConfig(platform)}
                    >
                      <Icon name="settings" />
                      {t('delivery.configure')}
                    </button>
                  </>
                ) : (
                  <div className="platform-coming-soon">
                    <span>{t('delivery.config.comingSoon')}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer informativo: plataformas en proceso de integracion comercial */}
      {!gateInfo && (
        <p className="platforms-upcoming">{t('delivery.platforms.upcoming')}</p>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedPlatform && (
        <div className="modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="modal-content config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('delivery.config.title', { platform: selectedPlatform.name })}</h2>
              <button type="button" className="close-btn" onClick={() => setShowConfigModal(false)}>
                <Icon name="close" />
              </button>
            </div>

            <form onSubmit={handleSaveConfig}>
              {/* Mercado Envíos reuses MercadoPago credentials — show setup note */}
              {selectedPlatform.reusesMpCredentials && (
                <div className="alert alert-info config-mp-note">
                  <Icon name="info" />
                  <div>
                    <p>{t('delivery.platforms.mercadoEnvios.description')}</p>
                    <p className="form-help">
                      {t('delivery.platforms.mercadoEnvios.requiresMpSetup')}
                    </p>
                  </div>
                </div>
              )}

              {/* Enabled Toggle */}
              <div className="form-group">
                <label>{t('delivery.config.status')}</label>
                <div className="toggle-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="enabled"
                      checked={!configData.enabled}
                      onChange={() => setConfigData({ ...configData, enabled: false })}
                    />
                    <span>{t('delivery.config.disabled')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="enabled"
                      checked={configData.enabled}
                      onChange={() => setConfigData({ ...configData, enabled: true })}
                    />
                    <span>{t('delivery.config.enabled')}</span>
                  </label>
                </div>
              </div>

              {/* External Store ID — no aplica a Mercado Envíos (reusa credenciales MP) */}
              {!selectedPlatform.reusesMpCredentials && (
                <div className="form-group">
                  <label htmlFor="externalStoreId">{t('delivery.config.externalStoreId')}</label>
                  <input
                    type="text"
                    id="externalStoreId"
                    value={configData.externalStoreId}
                    onChange={(e) =>
                      setConfigData({ ...configData, externalStoreId: e.target.value })
                    }
                    placeholder="STORE-123456"
                  />
                  <p className="form-help">{t('delivery.config.externalStoreIdHelp')}</p>
                </div>
              )}

              {/* Auto Accept */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={configData.autoAccept}
                    onChange={(e) => setConfigData({ ...configData, autoAccept: e.target.checked })}
                  />
                  <span>{t('delivery.config.autoAccept')}</span>
                </label>
                <p className="form-help">{t('delivery.config.autoAcceptHelp')}</p>
              </div>

              {/* Default Prep Time */}
              <div className="form-group">
                <label htmlFor="defaultPrepTime">{t('delivery.config.defaultPrepTime')}</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    id="defaultPrepTime"
                    value={configData.defaultPrepTime}
                    onChange={(e) =>
                      setConfigData({
                        ...configData,
                        defaultPrepTime: parseInt(e.target.value) || 25,
                      })
                    }
                    min="5"
                    max="120"
                  />
                  <span className="input-suffix">min</span>
                </div>
              </div>

              {/* Commission / Mode — no aplican a Mercado Envíos (reusa credenciales MP) */}
              {!selectedPlatform.reusesMpCredentials && (
                <>
                  {/* Commission Percent */}
                  <div className="form-group">
                    <label htmlFor="commissionPercent">
                      {t('delivery.config.commissionPercent')}
                    </label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        id="commissionPercent"
                        value={configData.commissionPercent}
                        onChange={(e) =>
                          setConfigData({
                            ...configData,
                            commissionPercent: parseFloat(e.target.value) || 0,
                          })
                        }
                        min="0"
                        max="100"
                        step="0.5"
                      />
                      <span className="input-suffix">%</span>
                    </div>
                    <p className="form-help">{t('delivery.config.commissionPercentHelp')}</p>
                  </div>

                  {/* Mode */}
                  <div className="form-group">
                    <label htmlFor="integrationMode">{t('delivery.config.mode')}</label>
                    <select
                      id="integrationMode"
                      value={configData.mode}
                      onChange={(e) => setConfigData({ ...configData, mode: e.target.value })}
                    >
                      <option value="marketplace">{t('delivery.config.modes.marketplace')}</option>
                      <option value="logistic">{t('delivery.config.modes.logistic')}</option>
                    </select>
                    <p className="form-help">{t('delivery.config.modeHelp')}</p>
                  </div>
                </>
              )}

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowConfigModal(false)}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <Icon name="sync" className="spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryIntegrationsPage;
